import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';
import type { CanvasserPerformanceResponse, CanvasserPerformanceShift } from '@signalvote/shared';
import { Skeleton } from '../ui/skeleton';
import { CollapsibleSection } from './CollapsibleSection';

interface PerformanceTrendChartProps {
  data: CanvasserPerformanceResponse | undefined;
  isLoading: boolean;
}

function formatDate(dateStr: string, options: Intl.DateTimeFormatOptions): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', options);
}

function CustomTooltip({ active, payload, label }: {
  active?: boolean;
  payload?: Array<{ payload: CanvasserPerformanceShift }>;
  label?: string;
}) {
  if (!active || !payload?.length || !label) return null;
  const shift = payload[0].payload;
  return (
    <div className="rounded border bg-background p-2 text-xs shadow-sm">
      <p className="font-medium">{formatDate(label, { month: 'long', day: 'numeric', year: 'numeric' })}</p>
      <p>Doors: {shift.doorsKnocked}</p>
      <p>Hours: {shift.hoursWorked.toFixed(1)}</p>
      <p>Doors/hr: {shift.doorsPerHour.toFixed(1)}</p>
    </div>
  );
}

export function PerformanceTrendChart({ data, isLoading }: PerformanceTrendChartProps) {
  if (isLoading) {
    return (
      <CollapsibleSection sectionId="performance-trend" title="Performance Trend">
        <Skeleton className="h-[300px] w-full" />
      </CollapsibleSection>
    );
  }

  const shifts = data?.shifts ?? [];

  return (
    <CollapsibleSection sectionId="performance-trend" title="Performance Trend">
      {shifts.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          No completed shifts with time data
        </p>
      ) : (
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={shifts}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(d: string) => formatDate(d, { month: 'short', day: 'numeric' })}
              />
              <YAxis label={{ value: 'Doors/hr', angle: -90, position: 'insideLeft' }} />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="doorsPerHour"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ r: 4, fill: '#3b82f6' }}
              />
              {data?.benchmarkDoorsPerHour != null && (
                <ReferenceLine
                  y={data.benchmarkDoorsPerHour}
                  stroke="#9ca3af"
                  strokeDasharray="6 4"
                  label={`Campaign avg: ${data.benchmarkDoorsPerHour.toFixed(1)}`}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </CollapsibleSection>
  );
}
